import type { FC } from 'react';
import type { Language } from '@/types/locales';
import { Button, Flex, Icon, LanguageSelector, Navbar, Typography } from '@ui';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { usePageContext } from 'vike-react/usePageContext';
import { reload } from 'vike/client/router';
import { authDelete } from '@/api/core/auth/authDelete';
import { useNavigate } from '@/hooks/useNavigate';
import { getAuthUrlWithRedirect } from '@/utils/getAuthUrlWithRedirect';
import { getLinkHref } from '@/utils/getLinkHref';
import styles from './Navigation.module.css';

export const Navigation: FC = () => {
    const { t, i18n } = useTranslation();
    const pageContext = usePageContext();
    const navigate = useNavigate();

    const onLogout = () => {
        authDelete()
            .then(() => reload())
            .catch(e => console.error(e));
    };

    const onLanguageChange = (language: Language) => {
        const expiresAt = dayjs().add(1, 'year').toString();
        pageContext.isClientSide && (document.cookie = `language=${language}; expires=${expiresAt}; secure; path=/`);
        void navigate(pageContext.truePath, language, { keepScrollPosition: true });
    };

    return (
        <div className={styles.navigationWrapper}>
            <Navbar
                items={[
                    { label: t('Menu_Events'), href: getLinkHref(pageContext, '/#events')! },
                    { label: t('Menu_Airports'), href: getLinkHref(pageContext, '/#airports')! },
                    { label: t('Menu_Contacts'), href: getLinkHref(pageContext, '/#contacts')! }
                ]}
                extraRight={(
                    <Flex align="center" gap={16}>
                        <LanguageSelector
                            value={i18n.language as Language}
                            onChange={onLanguageChange}
                        />
                        {pageContext.user
                            ? (
                                    <>
                                        <Typography.Paragraph>
                                            {`${pageContext.user.first_name} ${pageContext.user.last_name}`}
                                        </Typography.Paragraph>
                                        <Button icon={<Icon.Logout />} onClick={onLogout} />
                                    </>
                                )
                            : (
                                    <Button href={getAuthUrlWithRedirect(getLinkHref(pageContext, pageContext.truePath))}>
                                        {t('Menu_Login')}
                                    </Button>
                                )}
                    </Flex>
                )}
            />
        </div>
    );
};
